import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { menuHighlights, menuLookupKeys } from "../data/mock";
import { X, Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 60_000; // Re-fetch fresh activity once a minute.
const ROTATION_MS = 8_000; // Advance the visible toast every 8s.
const FIRST_SHOW_DELAY = 12_000; // Wait after page load before first toast.
const DISMISS_KEY = "mm-activity-dismissed";

/**
 * LiveActivityToast — bottom-left "Sarah just ordered Fish & Plantains" toast
 * that rotates through recent anonymised orders.
 *
 * Deliberate design choices:
 *  - Bottom-LEFT so it doesn't collide with the sticky cart drawer / order bar.
 *  - Dismiss button persists for 24h via localStorage.
 *  - Silently no-ops if the API returns 0 activity so it never shows an empty toast.
 *  - Skips rendering on back-office / print / kitchen routes (parent decides).
 */
export default function LiveActivityToast() {
  const [items, setItems] = useState([]);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(() => wasDismissedRecently());
  const [index, setIndex] = useState(0);

  // Resolve lookup_key → dish display name using the static menu map so the
  // toast can render synchronously without a second network call.
  const nameByKey = useMemo(() => {
    const m = new Map();
    for (const [id, key] of Object.entries(menuLookupKeys)) {
      const item = menuHighlights.find((x) => x.id === Number(id));
      if (item) m.set(key, item.name);
    }
    return m;
  }, []);

  // Fetch on mount + every minute.
  useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/orders/recent-activity?limit=8`);
        if (!cancelled) setItems(Array.isArray(data.items) ? data.items : []);
      } catch {
        /* silent — no toast is better than an error toast */
      }
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [dismissed]);

  // First-show delay so it doesn't interrupt the hero video.
  useEffect(() => {
    if (dismissed || items.length === 0) {
      setVisible(false);
      return;
    }
    const t = setTimeout(() => setVisible(true), FIRST_SHOW_DELAY);
    return () => clearTimeout(t);
  }, [items.length, dismissed]);

  // Rotate through items every ROTATION_MS.
  useEffect(() => {
    if (!visible || items.length <= 1) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % items.length), ROTATION_MS);
    return () => clearInterval(t);
  }, [visible, items.length]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* private mode */ }
    setDismissed(true);
    setVisible(false);
  };

  if (dismissed || !visible || items.length === 0) return null;

  const it = items[index] || items[0];
  const dishName = nameByKey.get(it.lookup_key) || prettify(it.lookup_key);

  return (
    <div
      className="fixed z-[70] bottom-6 left-4 sm:left-6 max-w-[92vw] sm:max-w-[340px] animate-[slideInLeft_0.4s_ease-out]"
      data-testid="live-activity-toast"
    >
      <div className="relative bg-[var(--mm-charcoal)]/95 backdrop-blur-md border border-[var(--mm-cream)]/12 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.25)] pl-4 pr-8 py-3 flex items-center gap-3">
        <div className="w-2 h-2 rounded-full bg-[var(--mm-accent)] shrink-0 animate-pulse" />
        <div className="min-w-0 flex-1">
          <p className="text-[0.62rem] tracking-[0.28em] uppercase text-[var(--mm-accent)] mb-0.5 flex items-center gap-1.5">
            <Flame size={9} /> Just now
          </p>
          <p className="text-[13px] text-[var(--mm-cream)] leading-snug truncate">
            <span className="font-medium">{it.first_name}</span> ordered{" "}
            <span className="italic">{dishName}</span>
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          data-testid="live-activity-dismiss"
          className="absolute top-2 right-2 text-[var(--mm-cream)]/40 hover:text-[var(--mm-cream)] transition-colors"
        >
          <X size={12} />
        </button>
      </div>
    </div>
  );
}

function wasDismissedRecently() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const then = parseInt(raw, 10);
    if (!then) return false;
    return Date.now() - then < 24 * 60 * 60 * 1000; // 24h
  } catch {
    return false;
  }
}

function prettify(key) {
  if (!key) return "one of mama's dishes";
  return key
    .replace(/^mamamina_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
